import { Box, Button, Chip, Grid, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useState } from 'react';
import {
  Link as RouterLink,
  Navigate,
  NavLink,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  useRouteLoaderData,
} from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { isDeployProblem } from '@/api/routingDeploy';
import type {
  AppRole,
  AppTopology,
  BrandDto,
  PlatformDefaultsDto,
  ProductDto,
  TenantDto,
} from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ExternalAuthMuted } from '@/components/ExternalAuthMuted';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { deployProblemText } from '@/components/routingDeployText';
import { useCurrentOperator } from '@/auth/AuthContext';
import { GhlMarketplaceApp } from './GhlMarketplaceApp';
import { ProductAdmins } from './ProductAdmins';
import { ProductApps } from './ProductApps';
import { ProductInternalKeys } from './ProductInternalKeys';
import { ProductLimitDefinitions } from './ProductLimitDefinitions';
import { ProductAgentClients } from './ProductAgentClients';
import { ProductOidcClients } from './ProductOidcClients';
import { ProductPlans } from './ProductPlans';
import { ProductResellerAdmin } from './ProductResellerAdmin';

// SASADM-0108 U1 — one page of the `productId`-filtered tenant read is the whole
// "tenants in this product" list in practice (a product's tenant set is well
// under the server's max page size), so this asks for that max rather than
// paging the section. If a product does exceed it, the section shows a
// "showing first N" caption.
const TENANTS_ON_PRODUCT_SIZE = 200;

export interface ProductDetailLoaderData {
  product: ProductDto;
  brands: BrandDto[];
  /** The first page of this product's tenants (see `TENANTS_ON_PRODUCT_SIZE`). */
  tenants: TenantDto[];
  /** How many tenants the product has in total — `tenants.length` unless truncated. */
  tenantsTotal: number;
}

interface ProductTabSpec {
  value: string;
  label: string;
  fallback: string;
  superAdmin?: boolean;
}

const PRODUCT_TABS: readonly ProductTabSpec[] = [
  { value: 'apps', label: 'products.tabs.apps', fallback: 'Apps' },
  {
    value: 'internal-keys',
    label: 'products.tabs.internalKeys',
    fallback: 'Internal keys',
    superAdmin: true,
  },
  { value: 'limits', label: 'products.tabs.limits', fallback: 'Limits', superAdmin: true },
  { value: 'plans', label: 'products.tabs.plans', fallback: 'Plans', superAdmin: true },
  {
    value: 'oidc',
    label: 'products.tabs.oidc',
    fallback: 'Connected Apps (OIDC)',
    superAdmin: true,
  },
  { value: 'agents', label: 'products.tabs.agents', fallback: 'Agents', superAdmin: true },
  {
    value: 'ghl-marketplace',
    label: 'products.tabs.ghlMarketplace',
    fallback: 'GHL Marketplace',
    superAdmin: true,
  },
];

/** SASADM-0151 — Apps sub-tab path segment + copy. */
interface AppsSubTabSpec {
  value: string;
  label: string;
  fallback: string;
  /** App-role panel; omit for Reseller Admin / Product Admin. */
  role?: AppRole;
  superAdmin?: boolean;
  /** SASADM-0152 — hidden on SELF_CONTAINED products (Reseller Admin). */
  composedOnly?: boolean;
}

const APPS_SUB_TABS: readonly AppsSubTabSpec[] = [
  {
    value: 'public',
    label: 'products.apps.subTabs.public',
    fallback: 'Public Apps',
    role: 'PUBLIC',
  },
  {
    value: 'tenant-admin',
    label: 'products.apps.subTabs.tenantAdmin',
    fallback: 'Tenant Admin',
    role: 'TENANT_ADMIN',
  },
  {
    value: 'api',
    label: 'products.apps.subTabs.api',
    fallback: 'API',
    role: 'API',
  },
  // SASADM-0172 (SASATH-0367) — the singleton AFFILIATE app; COMPOSED products only.
  {
    value: 'affiliate',
    label: 'products.apps.subTabs.affiliate',
    fallback: 'Affiliate',
    role: 'AFFILIATE',
    composedOnly: true,
  },
  {
    value: 'self-contained',
    label: 'products.apps.subTabs.selfContained',
    fallback: 'Self-contained',
    role: 'SELF_CONTAINED',
  },
  {
    value: 'reseller-admin',
    label: 'products.apps.subTabs.resellerAdmin',
    fallback: 'Reseller Admin',
    superAdmin: true,
    composedOnly: true,
  },
  {
    value: 'product-admin',
    label: 'products.apps.subTabs.productAdmin',
    fallback: 'Product Admin',
    superAdmin: true,
  },
];

function topologyAllowsAppsSubTab(topology: AppTopology, tab: AppsSubTabSpec): boolean {
  if (tab.composedOnly) return topology !== 'SELF_CONTAINED';
  if (!tab.role) return true;
  return topology === 'SELF_CONTAINED'
    ? tab.role === 'SELF_CONTAINED'
    : tab.role !== 'SELF_CONTAINED';
}

export function visibleAppsSubTabs(
  topology: AppTopology,
  isSuperAdmin: boolean,
): readonly AppsSubTabSpec[] {
  return APPS_SUB_TABS.filter(
    (tab) => topologyAllowsAppsSubTab(topology, tab) && (!tab.superAdmin || isSuperAdmin),
  );
}

export async function productDetailLoader({
  params,
}: {
  params: { id?: string };
}): Promise<ProductDetailLoaderData> {
  const id = Number(params.id);
  // SASATH-0212 `productId` filter — the server selects this product's tenants,
  // replacing a full-table fetch that was filtered client-side.
  const [products, brands, tenantsPage] = await Promise.all([
    apiAdmin.products.list(),
    apiAdmin.brands.list(id),
    apiAdmin.tenants.listPaged({ productId: id, size: TENANTS_ON_PRODUCT_SIZE }),
  ]);
  const product = products.find((p) => p.id === id);
  if (!product) throw new Response('Not found', { status: 404 });
  return {
    product,
    brands,
    tenants: tenantsPage.content,
    tenantsTotal: tenantsPage.totalElements,
  };
}

export function ProductDetail() {
  const { product, brands, tenants, tenantsTotal } = useLoaderData() as ProductDetailLoaderData;
  const isExternal = product.authMode === 'EXTERNAL';
  const { t } = useCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Spec 0037 — endpoint-driven platform line (null until saas-auth 0038 → static fallback).
  const [platform, setPlatform] = useState<PlatformDefaultsDto | null>(null);

  const pathSegments = location.pathname.split('/').filter(Boolean);
  // SASADM-0151 — top-level tab is the segment immediately after the product id
  // (e.g. `apps` in `/reference/products/1/apps/public`), not the last segment.
  const productsIdx = pathSegments.indexOf('products');
  const requestedTab = pathSegments[productsIdx + 2] ?? 'apps';
  const activeTab = PRODUCT_TABS.find((tab) => tab.value === requestedTab)?.value ?? 'apps';
  const activeTabSpec = PRODUCT_TABS.find((tab) => tab.value === activeTab);
  const activeTabAllowed = !activeTabSpec?.superAdmin || Boolean(operator?.isSuperAdmin);

  useEffect(() => {
    if (!activeTabAllowed) {
      navigate(`/reference/products/${product.id}/apps`, { replace: true });
    }
  }, [activeTabAllowed, navigate, product.id]);

  useEffect(() => {
    void apiAdmin.platformDefaults.get().then(setPlatform);
  }, []);

  const defaultBrand =
    product.defaultBrandId != null
      ? brands.find((b) => b.id === product.defaultBrandId)
      : undefined;

  async function onDelete() {
    setError(null);
    setPending(true);
    try {
      const outcome = await apiAdmin.products.delete(product.id);
      // SASADM-0176 — the page navigates away, so a problem deploy is a warning snackbar.
      if (isDeployProblem(outcome)) snack.warning(deployProblemText(outcome, t));
      else snack.success(t('snackbar.productDeleted', 'Product deleted.'));
      navigate('/reference/products');
    } catch (err) {
      if (err instanceof ApiError) {
        // 409 PRODUCT_IN_USE (and any other failure) — surface the backend
        // message via the banner and stay on the page.
        setError(err);
        setConfirmDelete(false);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.reference', 'Reference') },
          { label: t('nav.products', 'Products'), to: '/reference/products' },
          { label: product.displayName },
        ]}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h1">{product.displayName}</Typography>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {product.slug}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to={`/reference/products/${product.id}/edit`}
            variant="contained"
            startIcon={<EditIcon />}
          >
            {t('common.edit', 'Edit')}
          </Button>
          <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)}>
            {t('common.delete', 'Delete')}
          </Button>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Chip label={`aud: ${product.jwtAudience}`} size="small" />
        <Chip
          label={`MFA: ${t(`products.mfa.${product.defaultMfaPolicy}`, product.defaultMfaPolicy)}`}
          size="small"
        />
        <Chip
          label={t(
            `products.authModes.${product.authMode ?? 'PLATFORM'}`,
            product.authMode ?? 'PLATFORM',
          )}
          size="small"
          color={product.authMode === 'EXTERNAL' ? 'warning' : 'default'}
        />
        {product.appTopology === 'SELF_CONTAINED' ? (
          <Chip
            label={t('products.packaging.SELF_CONTAINED', 'Self-contained')}
            size="small"
            color="default"
          />
        ) : null}
      </Stack>
      <ErrorBanner
        error={error}
        message={error?.code === 'PRODUCT_IN_USE' ? (error.details.message ?? null) : null}
      />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('products.detail.configuration', 'Configuration')}
        </Typography>
        <Grid container spacing={2}>
          <Field
            label={t('products.fields.defaultMfaReverifyDays', 'Reverify days')}
            value={String(product.defaultMfaReverifyDays)}
          />
          <Field
            label={t('products.fields.defaultMfaTrustedDeviceDays', 'Trusted-device days')}
            value={String(product.defaultMfaTrustedDeviceDays)}
          />
          <Field
            label={t('products.fields.defaultBrandId', 'Default brand')}
            value={
              product.defaultBrandId == null
                ? t('products.fields.defaultBrandNone', '— None —')
                : defaultBrand
                  ? `${defaultBrand.displayName} (${defaultBrand.slug})`
                  : `#${product.defaultBrandId}`
            }
          />
          <Field
            label={t('products.fields.guestSignupUrl', 'Guest signup URL')}
            value={product.guestSignupUrl ?? ''}
          />
          <Field
            label={t('products.fields.guestSignupLabel', 'Guest signup label')}
            value={
              product.guestSignupLabel ||
              (product.guestSignupUrl
                ? t('products.detail.guestLabelDefault', 'Continue as guest (default)')
                : '')
            }
          />
          <Field
            label={t('products.fields.guestVerificationEnabled', 'Enable guest email verification')}
            value={product.guestVerificationEnabled ? t('common.yes', 'Yes') : t('common.no', 'No')}
          />
          <Field
            label={t(
              'products.fields.productAdminMfaRequired',
              'Require MFA for product administrators',
            )}
            value={product.productAdminMfaRequired ? t('common.yes', 'Yes') : t('common.no', 'No')}
          />
          <Field
            label={t('products.fields.resellerMfaPolicy', 'Reseller MFA policy')}
            value={t(
              `products.fields.resellerMfaPolicyOptions.${product.resellerMfaPolicy ?? 'REQUIRED'}`,
              product.resellerMfaPolicy ?? 'REQUIRED',
            )}
          />
          {/* SASADM-0172 (SASATH-0388 §2) */}
          <Field
            label={t(
              'products.fields.selfServiceTenantCreate',
              'Allow self-service tenant creation',
            )}
            value={product.selfServiceTenantCreate ? t('common.yes', 'Yes') : t('common.no', 'No')}
          />
        </Grid>
        {/* Spec 0037 — one endpoint-driven platform line; falls back to the 0035 static
            text until saas-auth 0038's platform-defaults endpoint ships. */}
        <ExternalAuthMuted
          muted={isExternal}
          note={t(
            'externalAuth.notes.login',
            "This product authenticates externally — platform login/branding guidance doesn't apply.",
          )}
        >
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {platform
                ? t(
                    'products.detail.platformLine',
                    'Platform: {{auth}}{{login}} · mail from {{mail}}',
                    {
                      auth: platform.authHostname,
                      login: platform.loginHostname ? ` · ${platform.loginHostname}` : '',
                      mail: platform.emailFromAddress,
                    },
                  )
                : t(
                    'products.detail.platformAuthLine',
                    'Auth: auth.journix.ai · Login: login.journix.ai — platform services',
                  )}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              {t(
                'products.detail.authLoginGuidance',
                "To brand auth/login for a customer, add an AUTH/LOGIN-type hostname on the Tenant's Hostnames tab (the customer CNAMEs it to the platform host). There is no product-level auth/login setting.",
              )}
            </Typography>
          </Box>
        </ExternalAuthMuted>
      </Paper>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ExternalAuthMuted
            muted={isExternal}
            note={t(
              'externalAuth.notes.brands',
              "This product authenticates externally — tenant brands aren't applied to its login.",
            )}
          >
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="h3" gutterBottom>
                Brands
              </Typography>
              {brands.length === 0 ? (
                <Typography
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No brands in this product.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {brands.map((b) => (
                    <Typography
                      key={b.id}
                      component={RouterLink}
                      to={`/brands/${b.id}`}
                      variant="body2"
                    >
                      {b.displayName} ({b.slug})
                    </Typography>
                  ))}
                </Stack>
              )}
            </Paper>
          </ExternalAuthMuted>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="h3" gutterBottom>
              Tenants
            </Typography>
            {tenants.length === 0 ? (
              <Typography
                sx={{
                  color: 'text.secondary',
                }}
              >
                No tenants in this product.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {tenants.map((tn) => (
                  <Typography
                    key={tn.id}
                    component={RouterLink}
                    to={`/tenants/${tn.id}`}
                    variant="body2"
                  >
                    {tn.name} ({tn.slug})
                  </Typography>
                ))}
              </Stack>
            )}
            {tenantsTotal > tenants.length ? (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 1,
                }}
              >
                {t('products.detail.tenantsTruncated', 'Showing first {{n}} of {{total}}.', {
                  n: tenants.length,
                  total: tenantsTotal,
                })}
              </Typography>
            ) : null}
          </Paper>
        </Grid>
      </Grid>
      <Tabs
        value={activeTabAllowed ? activeTab : 'apps'}
        onChange={(_event, value) => navigate(`/reference/products/${product.id}/${value}`)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={t('products.tabs.ariaLabel', 'Product sections')}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {PRODUCT_TABS.filter((tab) => !tab.superAdmin || operator?.isSuperAdmin).map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={t(tab.label, tab.fallback)}
            component={NavLink}
            to={tab.value}
          />
        ))}
      </Tabs>
      {activeTabAllowed ? <Outlet /> : null}
      <ConfirmDialog
        open={confirmDelete}
        title={t('products.editor.delete', 'Delete product')}
        body={t('products.editor.deleteBody', 'Delete {{name}}? This cannot be undone.', {
          name: product.displayName,
        })}
        severity="error"
        requireTypedConfirmation={product.displayName}
        busy={pending}
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function useProductDetailData(): ProductDetailLoaderData {
  return useRouteLoaderData('product-detail') as ProductDetailLoaderData;
}

/** SASADM-0151 — Apps layout: role sub-tab strip + outlet for the selected panel. */
export function ProductAppsLayout() {
  const { product } = useProductDetailData();
  const { t } = useCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const operator = useCurrentOperator();
  const topology = product.appTopology ?? 'COMPOSED';
  const isSuperAdmin = Boolean(operator?.isSuperAdmin);
  const subTabs = visibleAppsSubTabs(topology, isSuperAdmin);
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1] ?? '';
  const firstSub = subTabs[0]?.value ?? 'public';
  const activeSub = subTabs.find((tab) => tab.value === lastSegment)?.value ?? firstSub;
  const subTabAllowed = subTabs.some((tab) => tab.value === lastSegment);

  useEffect(() => {
    if (!subTabAllowed) {
      navigate(`/reference/products/${product.id}/apps/${firstSub}`, { replace: true });
    }
  }, [subTabAllowed, navigate, product.id, firstSub]);

  return (
    <Box>
      <Tabs
        value={subTabAllowed ? activeSub : firstSub}
        onChange={(_event, value) => navigate(`/reference/products/${product.id}/apps/${value}`)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={t('products.apps.subTabs.ariaLabel', 'App roles')}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {subTabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={t(tab.label, tab.fallback)}
            component={NavLink}
            to={tab.value}
          />
        ))}
      </Tabs>
      {subTabAllowed ? <Outlet /> : null}
    </Box>
  );
}

export function ProductAppsIndexRedirect() {
  const { product } = useProductDetailData();
  const operator = useCurrentOperator();
  const first =
    visibleAppsSubTabs(product.appTopology ?? 'COMPOSED', Boolean(operator?.isSuperAdmin))[0]
      ?.value ?? 'public';
  return <Navigate to={first} replace />;
}

export function ProductAppsTab({ appRole }: { appRole: AppRole }) {
  const { product } = useProductDetailData();
  return (
    <ProductApps
      productId={product.id}
      productSlug={product.slug}
      appTopology={product.appTopology ?? 'COMPOSED'}
      appRole={appRole}
    />
  );
}

export function ProductResellerAdminTab() {
  const { product } = useProductDetailData();
  return (
    <ProductResellerAdmin
      productId={product.id}
      productSlug={product.slug}
      baseDomain={product.baseDomain}
    />
  );
}

export function ProductAdminsTab() {
  const { product } = useProductDetailData();
  return (
    <ProductAdmins
      productId={product.id}
      productSlug={product.slug}
      appTopology={product.appTopology ?? 'COMPOSED'}
    />
  );
}

export function ProductInternalKeysTab() {
  const { product } = useProductDetailData();
  return <ProductInternalKeys productId={product.id} />;
}

export function ProductLimitDefinitionsTab() {
  const { product } = useProductDetailData();
  return <ProductLimitDefinitions productId={product.id} />;
}

export function ProductPlansTab() {
  const { product } = useProductDetailData();
  return <ProductPlans productId={product.id} />;
}

export function ProductOidcClientsTab() {
  const { product } = useProductDetailData();
  const { t } = useCopy();
  return (
    <ExternalAuthMuted
      muted={product.authMode === 'EXTERNAL'}
      note={t(
        'externalAuth.notes.oidc',
        "This product authenticates externally — OIDC clients aren't used here.",
      )}
    >
      <ProductOidcClients productId={product.id} />
    </ExternalAuthMuted>
  );
}

export function ProductAgentsTab() {
  const { product } = useProductDetailData();
  return <ProductAgentClients productId={product.id} productSlug={product.slug} />;
}

export function ProductGhlMarketplaceTab() {
  const { product } = useProductDetailData();
  return (
    <GhlMarketplaceApp
      productId={product.id}
      productSlug={product.slug}
      productDisplayName={product.displayName}
    />
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  );
}
